import React, { createContext, useContext } from "react";

interface InputFieldContextType {
	/**
	 * The id of the input
	 */
	id: string;
	/**
	 * The error message of the input
	 */
	error?: string;
	/**
	 * Whether the input is disabled
	 */
	disabled?: boolean;
	/**
	 * Whether the input is read-only
	 */
	readOnly?: boolean;
	/**
	 * The info message of the input. Can be a plain string or an object with title and message.
	 */
	info?: InputInfoContent;
	/**
	 * Whether to disable label click behavior (htmlFor attribute)
	 */
	disableLabelClick?: boolean;
}

export type InputInfoContent =
	| string
	| {
			title?: string;
			message: string;
			titleBottomLine?: boolean;
	  };

const InputFieldContext = createContext<InputFieldContextType | undefined>(undefined);

/**
 * Hook that returns the input field context
 */
export function useInputField() {
	const context = useContext(InputFieldContext);
	if (!context) {
		return {
			id: "",
			error: undefined,
			disabled: undefined,
			readOnly: undefined,
			info: undefined,
			disableLabelClick: undefined,
		};
	}
	return context;
}

interface InputFieldProviderProps {
	id: string;
	error?: string;
	disabled?: boolean;
	readOnly?: boolean;
	children: React.ReactNode;
	info?: InputInfoContent;
	disableLabelClick?: boolean;
}

export function InputFieldProvider({
	id,
	error,
	disabled,
	readOnly,
	children,
	info,
	disableLabelClick,
}: InputFieldProviderProps) {
	return (
		<InputFieldContext.Provider value={{ id, error, disabled, readOnly, info, disableLabelClick }}>
			{children}
		</InputFieldContext.Provider>
	);
}
